import React from "react";
import classNames from "classnames";
import { Link, LinkProps } from "react-router";

export interface Props {
  type?: "button" | "submit" | "reset";
  fullWidth?: boolean;
  handleClick?: React.MouseEventHandler<HTMLElement>;
  extraClasses?: string;
  buttonStyle?: string;
  size?: string;
  color?: string;
  buttonType?: "router" | "link" | "router-link";
  path?: string;
  icon?: string;
  showIcon?: boolean;
}

/*
  Because of how button is composed, we need to make sure our props support as
  close as possible the right combination of potential attributes.
*/

export type ButtonProps = Props &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "onClick"> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "type" | "href" | "onClick"> &
  Omit<LinkProps, "to" | "onClick">;

const Button: React.FC<ButtonProps> = (props: ButtonProps): JSX.Element => {
  const {
    fullWidth,
    type,
    handleClick,
    children,
    buttonStyle = "solid",
    size = "",
    color = "green",
    extraClasses,
    buttonType,
    path,
    showIcon = false,
    icon = "",
    ...rest
  } = props;

  const btnClass = classNames(
    "caprx-btn",
    { [`caprx-btn-${buttonStyle}`]: buttonStyle },
    { [`caprx-btn-${size}`]: size !== "" },
    { [`caprx-btn-${color}`]: color },
    { [`caprx-btn-fullWidth`]: fullWidth },
    extraClasses,
  );

  const buttonIcon = showIcon && <span className={`submitbtn-icon ${icon}`} />;

  let button = (
    <button
      {...rest as Partial<React.ButtonHTMLAttributes<HTMLButtonElement>>}
      className={btnClass}
      onClick={handleClick as React.MouseEventHandler<HTMLButtonElement>}
      type={type}
    >
      {buttonIcon}
      {children}
    </button>
  );

  if (buttonType === "link") {
    button = (
      <a
        {...rest as Partial<React.AnchorHTMLAttributes<HTMLAnchorElement>>}
        href={path}
        className={btnClass}
        onClick={handleClick as React.MouseEventHandler<HTMLAnchorElement>}
        type={type}
      >
        {buttonIcon}
        {children}
      </a>
    );
  }

  if (buttonType === "router-link") {
    button = (
      <Link
        {...rest as Partial<LinkProps>}
        to={path}
        className={btnClass}
        onClick={handleClick as React.MouseEventHandler<HTMLAnchorElement>}
      >
        {buttonIcon}
        {children}
      </Link>
    );
  }

  return button;
};

export default Button;
